import { forwardRef, type PropsWithChildren, useImperativeHandle, useRef, useState } from 'react';
import { type ReactZoomPanPinchContentRef, TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';

export type ImageZoomActions = Pick<ReactZoomPanPinchContentRef, 'zoomIn' | 'zoomOut' | 'resetTransform'> | undefined;

type ImageZoomPanProps = PropsWithChildren<{
  onImageZoom?: (isZooming: boolean) => void;
  src: string;
  isInView: boolean;
  className?: string;
}>;

const ZOOM_FACTOR = 10; // Ratio of enlargement
const STEP_COUNT = 11; // Times to zoom in

const ImageZoomPan = forwardRef<ImageZoomActions, ImageZoomPanProps>(
  ({ onImageZoom, isInView, children, src }, ref) => {
    const [scale, setScale] = useState(1);
    const transformWrapperRef = useRef<ReactZoomPanPinchContentRef>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const maxScale = ZOOM_FACTOR;
    const zoomStep = maxScale ** (1 / STEP_COUNT) - 1;

    useImperativeHandle(ref, () => {
      if (!transformWrapperRef.current) return undefined;
      const { zoomIn, zoomOut, resetTransform } = transformWrapperRef.current;
      return {
        zoomIn: (step?: number) => zoomIn(step ?? zoomStep),
        zoomOut: (step?: number) => zoomOut(step ?? zoomStep),
        resetTransform,
      };
    }, []);

    if (!isInView) return null;

    return (
      <div
        className='relative bg-black w-full h-full'
        ref={containerRef}>
        <TransformWrapper
          ref={transformWrapperRef}
          key={src}
          disabled={maxScale <= 1}
          centerOnInit
          initialScale={1}
          minScale={1}
          maxScale={maxScale}
          wheel={{ smoothStep: 0.01, step: zoomStep }}
          panning={{ disabled: scale === 1 }}
          onPinchingStart={() => {
            onImageZoom?.(true);
          }}
          doubleClick={{
            mode: scale !== 1 ? 'reset' : 'zoomIn',
            step: 0.65,
          }}
          onTransformed={({ state }) => {
            setScale(state.scale);
            onImageZoom?.(state.scale <= 1);
          }}
          onZoomStop={({ state }) => {
            if (state.scale < 1) {
              transformWrapperRef.current?.resetTransform();
            }
          }}>
          <TransformComponent wrapperClass=' !w-full !h-full'>{children}</TransformComponent>
        </TransformWrapper>
      </div>
    );
  },
);

export default ImageZoomPan;
